import { useState, forwardRef } from 'react'

import TimePicker from '../TimePicker'

import { generateCalendarDays, isToday, isSelected } from './calendar'
import { useValueEffect } from './effects'
import { handleDateSelect, handleMonthSelect, handleYearSelect, handlePresent, handleTimeChange, handleConfirm, handlePrevMonth, handleNextMonth, handlePrevYear, handleNextYear, handlePrevDecade, handleNextDecade, handleToday } from './handlers'
import { parseDate } from './parsing'
import { useCombinedRef } from './refs'
import { DatePickerProps, ViewMode } from './types'

export type { DatePickerProps, ViewMode }

// CSS class constants to avoid duplication
const SELECTED_DATE_CLASS = 'bg-blue-600 text-white'
const UNSELECTED_DATE_CLASS = 'text-gray-900 hover:bg-gray-100'
const BUTTON_PADDING_WITH_TIME = 'px-3 py-1.5'
const BUTTON_PADDING_WITHOUT_TIME = 'px-2 py-1'

const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(({ 
  value, 
  onChange, 
  onClose, 
  position, 
  includeTime = false, 
  monthYearOnly = false,
  allowPresent = false,
  onConfirm, 
  onQuickAction, 
  showRevert = false 
}, ref) => {

  const initialDate = parseDate(value, monthYearOnly, includeTime) || new Date()
  const [viewMode, setViewMode] = useState<ViewMode>(monthYearOnly ? 'month' : 'day')
  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth())
  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear())
  const [selectedDate, setSelectedDate] = useState<Date | null>(parseDate(value, monthYearOnly, includeTime))
  const [selectedMonth, setSelectedMonth] = useState<number | null>(monthYearOnly ? initialDate.getMonth() : null)
  const [selectedYear, setSelectedYear] = useState<number | null>(monthYearOnly ? initialDate.getFullYear() : null)
  const [selectedTime, setSelectedTime] = useState<{ hours: number; minutes: number }>(() => {
    const parsed = parseDate(value, monthYearOnly, includeTime)
    if (parsed && includeTime) {
      return { hours: parsed.getHours(), minutes: parsed.getMinutes() }
    }
    // Always use current time for timestamp fields when opening picker
    if (includeTime) {
      const now = new Date()
      return { hours: now.getHours(), minutes: now.getMinutes() }
    }
    return { hours: 0, minutes: 0 }
  })

  // Update selectedDate when value changes
  useValueEffect(value, includeTime, monthYearOnly, setSelectedDate, setCurrentMonth, setCurrentYear, setSelectedMonth, setSelectedYear, setSelectedTime)

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

  const handleDateSelectWrapper = (day: number, event?: React.MouseEvent) => {
    handleDateSelect(day, currentYear, currentMonth, includeTime, selectedTime, setSelectedDate, onChange, onClose, event)
  }

  const handleYearSelectWrapper = (year: number) => {
    handleYearSelect(year, monthYearOnly, selectedMonth, setSelectedYear, setCurrentYear, setViewMode, onChange, onClose)
  }

  const handleMonthSelectWrapper = (month: number) => {
    handleMonthSelect(
      month,
      monthYearOnly,
      selectedYear,
      currentYear,
      setSelectedMonth,
      setCurrentMonth,
      setSelectedYear,
      setViewMode,
      onChange,
      onClose
    )
  }

  const handlePresentWrapper = () => {
    handlePresent(onChange, onClose)
  }

  const handleTimeChangeWrapper = (hours: number, minutes: number) => {
    handleTimeChange(hours, minutes, selectedDate, setSelectedTime, setSelectedDate, setCurrentMonth, setCurrentYear)
  }

  const handleConfirmWrapper = () => {
    handleConfirm(selectedDate, selectedTime, includeTime, onChange, onConfirm, onClose)
  }

  const handlePrevMonthWrapper = () => {
    handlePrevMonth(currentMonth, currentYear, setCurrentMonth, setCurrentYear)
  }

  const handleNextMonthWrapper = () => {
    handleNextMonth(currentMonth, currentYear, setCurrentMonth, setCurrentYear)
  }

  const handlePrevYearWrapper = () => {
    handlePrevYear(currentYear, setCurrentYear)
  }

  const handleNextYearWrapper = () => {
    handleNextYear(currentYear, setCurrentYear)
  }

  const handleNextDecadeWrapper = () => {
    handleNextDecade(currentYear, setCurrentYear)
  }

  const handlePrevDecadeWrapper = () => {
    handlePrevDecade(currentYear, setCurrentYear)
  }

  const handleTodayWrapper = () => {
    handleToday(includeTime, setCurrentMonth, setCurrentYear, setSelectedTime, setSelectedDate, handleDateSelectWrapper)
  }


  const days = generateCalendarDays(currentMonth, currentYear)

  const isTodayWrapper = (day: number | null) => {
    return isToday(day, currentMonth, currentYear)
  }

  const isSelectedWrapper = (day: number | null) => {
    return isSelected(day, selectedDate, currentMonth, currentYear)
  }

  const [, combinedRef] = useCombinedRef<HTMLDivElement>(ref)
  
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={combinedRef}
      role="dialog"
      aria-modal="true"
      aria-label="Date picker"
      tabIndex={-1}
      className="fixed bg-white border border-gray-200 rounded-md shadow-lg z-50"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: includeTime ? '600px' : `${Math.max(position.width, 280)}px`,
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose()
        }
        e.stopPropagation()
      }}
    >
      {/* Main content: Calendar and Time Picker side by side */}
      <div className="flex">
        {/* Left: Calendar */}
        <div className="p-4 flex-shrink-0" style={{ width: includeTime ? '300px' : '100%' }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {viewMode === 'day' && (
                <button
                  onClick={() => setViewMode('month')}
                  className="font-semibold text-gray-900 hover:text-blue-600"
                >
                  {months[currentMonth]} {currentYear}
                </button>
              )}
              {viewMode === 'month' && (
                <button
                  onClick={() => setViewMode('year')}
                  className="font-semibold text-gray-900 hover:text-blue-600"
                >
                  {currentYear}
                </button>
              )}
              {viewMode === 'year' && (
                <span className="font-semibold text-gray-900">
                  {Math.floor(currentYear / 10) * 10} - {Math.floor(currentYear / 10) * 10 + 9}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {viewMode === 'day' && (
                <>
                  <button
                    onClick={handlePrevMonthWrapper}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={handleNextMonthWrapper}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </>
              )}
              {viewMode === 'month' && (
                <>
                  <button
                    onClick={handlePrevYearWrapper}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={handleNextYearWrapper}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </>
              )}
              {viewMode === 'year' && (
                <>
                  <button
                    onClick={handlePrevDecadeWrapper}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={handleNextDecadeWrapper}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Day View */}
          {viewMode === 'day' && (
            <>
              {/* Days of week */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {daysOfWeek.map((day, index) => (
                  <div
                    key={index}
                    className="text-center text-xs font-medium text-gray-500 py-1"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => {
                  if (day === null) {
                    return (
                      <div key={index} className="aspect-square"></div>
                    )
                  }

                  const todayClass = isTodayWrapper(day) ? 'border border-blue-500' : ''
                  const selectedClass = isSelectedWrapper(day)
                    ? SELECTED_DATE_CLASS
                    : UNSELECTED_DATE_CLASS

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={(e) => handleDateSelectWrapper(day, e)}
                      onMouseDown={(e) => e.stopPropagation()}
                      className={`aspect-square flex items-center justify-center text-sm rounded ${selectedClass} ${todayClass}`}
                    >
                      {day}
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {/* Month View */}
          {viewMode === 'month' && (
            <div className="grid grid-cols-3 gap-2">
              {months.map((month, index) => {
                const isSelectedMonth = monthYearOnly 
                  ? selectedMonth === index && (selectedYear === currentYear || selectedYear === null)
                  : selectedMonth === index && currentYear === selectedYear
                const isCurrentMonth = index === new Date().getMonth() && currentYear === new Date().getFullYear()
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleMonthSelectWrapper(index)}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={`px-3 py-2 text-sm rounded ${
                      isSelectedMonth
                        ? 'bg-blue-600 text-white'
                        : isCurrentMonth
                        ? 'border border-blue-500 text-gray-900 hover:bg-gray-100'
                        : 'text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {month.substring(0, 3)}
                  </button>
                )
              })}
            </div>
          )}

          {/* Year View */}
          {viewMode === 'year' && (
            <div className="grid grid-cols-4 gap-2">
              {Array.from({ length: 12 }, (_, i) => {
                const year = Math.floor(currentYear / 10) * 10 + i
                const isSelectedYear = selectedYear === year
                const isCurrentYear = year === new Date().getFullYear()
                return (
                  <button
                    key={year}
                    type="button"
                    onClick={() => handleYearSelectWrapper(year)}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={`px-3 py-2 text-sm rounded ${
                      isSelectedYear
                        ? SELECTED_DATE_CLASS
                        : isCurrentYear
                        ? `border border-blue-500 ${UNSELECTED_DATE_CLASS}`
                        : UNSELECTED_DATE_CLASS
                    }`}
                  >
                    {year}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Right: Time Picker (if includeTime is true) */}
        {includeTime && (
          <div 
            role="presentation"
            className="border-l border-gray-200 p-4 flex-shrink-0"
            style={{ width: '300px' }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <TimePicker
              hours={selectedTime.hours}
              minutes={selectedTime.minutes}
              onChange={handleTimeChangeWrapper}
            />
          </div>
        )}
      </div>

      {/* Footer - spans full width */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
        {/* Left side: Quick action buttons */}
        <div className={`flex gap-2 ${includeTime ? 'flex-wrap' : 'flex-nowrap'}`}>
          {allowPresent && (
            <button
              onClick={handlePresentWrapper}
              className={`${includeTime ? BUTTON_PADDING_WITH_TIME : BUTTON_PADDING_WITHOUT_TIME} text-xs font-medium bg-teal-100 text-teal-700 rounded hover:bg-teal-200 transition-colors whitespace-nowrap`}
            >
              I currently work here
            </button>
          )}
          {onQuickAction && !monthYearOnly && (
            <>
              <button
                onClick={() => onQuickAction('today')}
                className={`${includeTime ? BUTTON_PADDING_WITH_TIME : BUTTON_PADDING_WITHOUT_TIME} text-xs font-medium bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors whitespace-nowrap`}
              >
                Today
              </button>
              <button
                onClick={() => onQuickAction('+3d')}
                className={`${includeTime ? BUTTON_PADDING_WITH_TIME : BUTTON_PADDING_WITHOUT_TIME} text-xs font-medium bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors whitespace-nowrap`}
              >
                +3d
              </button>
              <button
                onClick={() => onQuickAction('+7d')}
                className={`${includeTime ? BUTTON_PADDING_WITH_TIME : BUTTON_PADDING_WITHOUT_TIME} text-xs font-medium bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors whitespace-nowrap`}
              >
                +7d
              </button>
              {showRevert && (
                <button
                  onClick={() => onQuickAction('revert')}
                  className={`${includeTime ? BUTTON_PADDING_WITH_TIME : BUTTON_PADDING_WITHOUT_TIME} text-xs font-medium bg-teal-100 text-teal-700 rounded hover:bg-teal-200 transition-colors whitespace-nowrap`}
                >
                  Revert to Auto
                </button>
              )}
            </>
          )}
        </div>
        {/* Right side: Confirm */}
        <div className="flex gap-2 flex-shrink-0">
          {!onQuickAction && !monthYearOnly && (
            <button
              onClick={handleTodayWrapper}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Today
            </button>
          )}
          {includeTime && (
            <button
              onClick={handleConfirmWrapper}
              className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
            >
              Confirm
            </button>
          )}
        </div>
      </div>
    </div>
  )
})

DatePicker.displayName = 'DatePicker'

export default DatePicker



